<script setup>
import moment from 'moment';
import 'moment/locale/bn';
import { computed, onMounted, reactive } from 'vue';
import { useLeads } from '@store/leads';
import { useUpgrade } from '@store/Upgrade';
import { useTranslation } from '../composables/useTranslation';

const { t } = useTranslation();

const $lead = useLeads();

onMounted(() => {
	const lang = (window.formychat_admin_vars?.site?.language || '').toLowerCase();
	if (lang.startsWith('bn')) {
		moment.locale('bn');
	}
});

const state = reactive({});

const props = defineProps({
	lead: Object,
});

// Google Sheets sync status
const isGoogleSheetsConnected = computed(() => {
	return window.formychat_admin_vars?.additional?.google_sheets?.connected || false;
});

const isSynced = computed(() => {
	return !!props.lead?.google_sheet_synced_at;
});

const lead = computed( () => {
	return props.lead || {};
})

const field = computed( () => {
	// If object, return values.
	return props.lead.field || {};
})

const meta = computed(() => {
    if (state.fullMeta) {
        return props.lead.meta || {}
    }

    // Get the meta object
    const fullMeta = props.lead.meta || {}
    
    // Return first 5 entries as a new object
    return Object.fromEntries(
        Object.entries(fullMeta).slice(0, 7)
    )
})

// Open details.
const details = (event) => {
	if (event.target.classList.contains('no-details') || event.target.closest('.no-details')) return;
	if ( lead.value.locked ) {
		state.showDetails = false
		useUpgrade().show()
		return;
	}

	state.showDetails = !state.showDetails;
}

const copyFormData = () => {
	state.formDataCopied = true

	// Copy to clipboard.
	const text = JSON.stringify(field.value, null, 2);
	navigator.clipboard.writeText(text);

	setTimeout(() => {
		state.formDataCopied = false
	}, 2000);
}

const copyDeviceInfo = () => {
	state.deviceInfoCopied = true

	// Copy to clipboard.
	const text = JSON.stringify(meta.value, null, 2);
	navigator.clipboard.writeText(text);

	setTimeout(() => {
		state.deviceInfoCopied = false
	}, 2000);
}

const emit = defineEmits( ['delete']);
const requestDelete = () => {

	if ( lead.value.locked ) {
		useUpgrade().show()
		return;
	}

	emit('delete', props.lead.id);
}

// Computed.
const excerpt = ( txt, l = 25 ) => {
	if ( ! txt ) return '';
	return txt.length > l ? txt.slice(0, l) + '...' : txt;
}

const multiline = ( txt ) => {
	if ( ! txt ) return '';
	return txt.replace(/\n/g, '<br>');
}

</script>
<template>
	<transition name="fade">
		<div class="flex flex-col w-full h-full border border-slate-100 border-t-0 divide-y divide-slate-100 transition-all overflow-hidden"
		v-if="!lead.deleted_at"
		:class="{'opacity-50 animate-pulse pointer-events-none' : lead.deleting}"

			@click="lead.locked ? useUpgrade().show() : null"
	>
		<div @click="details"
			class="w-full flex items-center child:py-2.5 child:px-5 last-child:text-right cursor-pointer hover:bg-gray-50 transition-all"
			:class="{
				'bg-gray-50' : state.showDetails,
				'blur-[2px] pointer-events-none' : lead.locked
			}"
			> 
			<div class="w-16 no-details"><input type="checkbox" class="bg-transparent" v-model="lead.selected" @input="$emit('select', lead)" /></div>
			<div class="w-1/6 break-words" v-if="$lead.columns[0]">{{ excerpt(field[$lead.columns[0]]) }}</div>
			<div class="w-1/6 break-words" v-if="$lead.columns[1]">{{ excerpt(field[$lead.columns[1]]) }}</div>
			<div class="w-1/6 break-words" v-if="$lead.columns[2]">{{ excerpt(field[$lead.columns[2]]) }}</div>
			<div class="w-2/6 break-words" v-if="$lead.columns[3]">{{ excerpt(field[$lead.columns[3]]) }}</div>
			<div class="w-2/6 break-words" v-if="$lead.filters.mode == 'formychat'">{{ $lead.getWidgetName( lead.widget_id ) }}</div>
			<div class="w-1/6 white sm:whitespace-nowrap">{{ moment(lead.created_at).calendar() }}</div>
			<div class="w-24 text-center" v-if="isGoogleSheetsConnected">
				<span v-if="isSynced" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
					{{ t('synced') }}
				</span>
				<span v-else class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
					{{ t('pending') }}
				</span>
			</div>
			<div class="w-[150px]">
				<div class="flex items-center justify-end gap-3">
					<button class="h-8 w-8 flex items-center justify-center  hover:opacity-90 transition cursor-pointer"
					:class="{
						'bg-indigo-100 text-indigo-600': !state.showDetails,
						'bg-indigo-500 text-indigo-50': state.showDetails,
					}">
					<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
						class="bi bi-pencil-fill" viewBox="0 0 16 16">
						<path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
						<path
							d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
						<path v-if="state.showDetails"
							d="M5.525 7.646a2.5 2.5 0 0 0 2.829 2.829zm4.95.708-2.829-2.83a2.5 2.5 0 0 1 2.829 2.829zm3.171 6-12-12 .708-.708 12 12z" />
					</svg>
				</button>
				<button
					@click="requestDelete"
					class="no-details active:bg-red-400 h-8 w-8 flex items-center justify-center bg-red-100 text-red-600 hover:bg-red-500 hover:text-white transition cursor-pointer">
					<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
						class="bi bi-trash3-fill" viewBox="0 0 16 16">
						<path
							d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5" />
					</svg>
				</button>
				</div>
			</div>
		</div>
		<transition name="slide-down">
			<div class="grid grid-cols-1 md:grid-cols-2 child:h-full" v-if="state.showDetails">
				<!-- form data  -->
				<div class="p-5 pr-0 md:pr-0 md:p-8 w-full flex flex-col gap-4">
					<h2 class="text-slate-500 font-medium">{{ t('form_submitted_data') }}</h2>

					<div class="bg-slate-50 py-5 px-6 h-full rounded border border-slate-100 relative">
						<span class="absolute right-4 top-4 flex items-center gap-2 cursor-pointer  transition" :class="{'text-indigo-500' : state.formDataCopied, 'text-slate-400 hover:text-slate-700' : !state.formDataCopied}" @click.prevent="copyFormData">
							<svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5" viewBox="0 0 16 16">
								<path v-if="!state.formDataCopied" d="M6.5 0A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0zm3 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5z"/>
								<path v-if="!state.formDataCopied" d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1A2.5 2.5 0 0 1 9.5 5h-3A2.5 2.5 0 0 1 4 2.5zm4.5 6V9H10a.5.5 0 0 1 0 1H8.5v1.5a.5.5 0 0 1-1 0V10H6a.5.5 0 0 1 0-1h1.5V7.5a.5.5 0 0 1 1 0"/>

								<path v-if="state.formDataCopied" d="M6.5 0A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0zm3 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5z"/>
  								<path v-if="state.formDataCopied" d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1A2.5 2.5 0 0 1 9.5 5h-3A2.5 2.5 0 0 1 4 2.5zm6.854 7.354-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 0 1 .708-.708L7.5 10.793l2.646-2.647a.5.5 0 0 1 .708.708"/>
							  </svg>
						</span>
						<div v-for="(value, key) in field" class="flex gap-4 w-full items-start mb-2 last:mb-0 text-[14px] 24 font-normal text-gray-400">
							<span class="capitalize whitespace-nowrap w-32 text-right">{{ key }} </span>
							:
							<span class="w-full text-gray-700 [&>a]:text-indigo-500 [&>a]:ring-0 [&>a]:hover:text-indigo-600 [&>a]:hover:underline [&>a]:transition-all [&>a]:cursor-pointer">
								<a v-if="key === 'email'" :href="`mailto:${value}`">{{value}}</a>
								<a v-else-if="key === 'phone'" :tel="value" :href="`tel:${value}`">{{value}}</a>
								<span v-else v-html="multiline(value)"></span>
							</span>
						</div>
					</div>

				</div>
				<!-- meta data  -->
				<div class="p-5 pr-0 md:pr-0 md:p-8 w-full flex flex-col gap-4">
					<h2 class="text-slate-500 font-medium">{{ t('others_information') }}</h2>

					<div class="bg-slate-50 p-6 rounded h-full border border-slate-100 relative">

						<span class="absolute right-12 top-4 flex items-center gap-2 cursor-pointer  transition" 
						:class="{'text-indigo-500' : state.deviceInfoCopied, 'text-slate-400 hover:text-slate-700' : !state.deviceInfoCopied}" 
						@click.prevent="copyDeviceInfo">
							<svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5" viewBox="0 0 16 16">
								<path v-if="!state.deviceInfoCopied" d="M6.5 0A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0zm3 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5z"/>
								<path v-if="!state.deviceInfoCopied" d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1A2.5 2.5 0 0 1 9.5 5h-3A2.5 2.5 0 0 1 4 2.5zm4.5 6V9H10a.5.5 0 0 1 0 1H8.5v1.5a.5.5 0 0 1-1 0V10H6a.5.5 0 0 1 0-1h1.5V7.5a.5.5 0 0 1 1 0"/>

								<path v-if="state.deviceInfoCopied" d="M6.5 0A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0zm3 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5z"/>
  								<path v-if="state.deviceInfoCopied" d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1A2.5 2.5 0 0 1 9.5 5h-3A2.5 2.5 0 0 1 4 2.5zm6.854 7.354-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 0 1 .708-.708L7.5 10.793l2.646-2.647a.5.5 0 0 1 .708.708"/>
							  </svg>
						</span>

						<!-- <div class="max-h-96 overflow-auto"> -->
							<div v-for="(value, key) in meta" class="flex gap-4 w-full items-start mb-2 last:mb-0 text-[14px] font-normal text-gray-400">
								<span class="capitalize whitespace-nowrap w-32 text-right">{{ key }}</span>
								:
								<span class="w-full text-gray-700 tracking-wide flex items-center gap-3 flex-wrap [&>img]:w-auto [&>img]:h-5 [&>img]:rounded" v-html="value"></span>
							</div>


							<div class="pl-10 mt-6 text-sm">
								<a class="text-indigo-500 hover:text-indigo-600 transition" href="javascript:;" 
								@click="state.fullMeta = !state.fullMeta">
									{{ state.fullMeta ? t('show_less') : t('show_more') }}	
								</a>
							</div>
						<!-- </div> -->

						<!-- Print JSON as String  -->
						<!-- <pre class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{{ JSON.stringify(meta, null, 2) }}</pre> -->
					</div>
				</div>
			</div>
		</transition>
	</div>
	</transition>
</template>


<style scoped>

.slide-down-enter-active {
	animation: slide-down 0.2s;
}

.slide-down-leave-active {
	animation: slide-down 0.3s reverse;
}


@keyframes slide-down {
	0% {
		opacity: 0;
		transform: translateY(-20px);
	}
	100% {
		opacity: 1;
		transform: translateY(0);
	}
}

</style>